// @ts-nocheck
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { ArrowLeft, Wand2, RotateCcw } from 'lucide-react';
import { 
  WizardFormData,
  CRISIS_PRINCIPLES,
  INDUSTRY_OPTIONS,
  AUDIENCE_OPTIONS,
  SCENARIO_OPTIONS,
  DURATION_OPTIONS,
  STAKEHOLDER_OPTIONS,
  COMPLIANCE_OPTIONS,
  COMMON_OBJECTIVES,
  RECOMMENDED_PRINCIPLE_ORDERS
} from '../types/wizard';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { GenerationProgress } from './GenerationProgress';

// Enhanced tooltips for user guidance
const OBJECTIVE_TOOLTIPS: Record<string, string> = {
  "Test crisis communication processes": "Evaluates how effectively participants use established communication channels, protocols, and messaging during a crisis. Includes testing both internal team communication and external stakeholder updates.",
  "Assess escalation procedures": "Examines participants' ability to recognize when decisions need to be escalated to higher authority levels, and whether they follow proper escalation chains and timelines.",
  "Evaluate decision-making under pressure": "Tests how participants gather information, weigh options, and make quality decisions when facing time pressure, incomplete information, and high stakes.",
  "Practice stakeholder management": "Develops skills in balancing the competing needs and concerns of different stakeholder groups (staff, customers, regulators, media, etc.) during crisis response.",
  "Validate business continuity plans": "Tests whether existing business continuity procedures work in practice and identifies gaps between documented plans and real-world execution.",
  "Test regulatory notification procedures": "Verifies participants understand when, how, and to whom they must report incidents according to regulatory requirements and compliance frameworks."
};

const STAKEHOLDER_TOOLTIPS: Record<string, string> = {
  "Staff": "Internal employees who need clear direction, safety information, and role clarity during the crisis. Includes both crisis team members and general workforce.",
  "Customers": "External clients who may be directly impacted by the crisis and need timely updates, alternative service arrangements, and reassurance about data/service security.",
  "Regulators": "Government agencies and industry bodies that require formal notification, compliance reporting, and may impose specific response requirements or timelines.",
  "Media": "News outlets and social media channels that can influence public perception and require proactive engagement to control narrative and prevent misinformation.",
  "Board": "Senior executives and board members who need strategic updates, approve major decisions, and may face public accountability for crisis response.",
  "Suppliers": "Third-party vendors and partners whose services may be disrupted and who need coordination to maintain critical business functions during the crisis."
};

const COMPLIANCE_TOOLTIPS: Record<string, string> = {
  "CPS230": "APRA's operational resilience standard requiring financial institutions to maintain comprehensive crisis management capabilities, including incident response, business continuity, and recovery procedures.",
  "ISO22301": "International standard for business continuity management systems, providing framework for preventing, preparing for, responding to, and recovering from disruptive incidents.",
  "AS/NZS 5050": "Australian/New Zealand standard for business continuity, covering risk management, incident response, and organizational resilience requirements.",
  "NIST Cybersecurity Framework": "US framework for managing cybersecurity risk, including incident response procedures, stakeholder communication, and recovery planning for cyber incidents."
};

export const ExerciseWizard: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState('');
  const [generationMode, setGenerationMode] = useState<'draft' | 'final'>('draft');
  const [progressStage, setProgressStage] = useState<'analyzing' | 'mapping' | 'generating' | 'building' | 'complete'>('analyzing');
  const [progressPercent, setProgressPercent] = useState(0);
  const { user } = useAuth();
  const navigate = useNavigate();

  const { register, control, handleSubmit, watch, setValue, formState: { errors } } = useForm<WizardFormData>({
    defaultValues: {
      industry: 'Health',
      geography: 'Australia - Victoria',
      audience: 'Crisis Management Team',
      scenarioType: 'Cyber',
      durationMinutes: 60,
      injectCount: 5,
      selectedPrinciples: [],
      objectives: [],
      riskDial: 3,
      stakeholders: [],
      complianceFrameworks: []
    }
  });

  const watchedValues = watch();
  const durationMinutes = watch('durationMinutes');
  const injectCount = watch('injectCount');
  const selectedPrinciples = watch('selectedPrinciples');

  // Auto-update inject count based on duration
  React.useEffect(() => {
    const durationOption = DURATION_OPTIONS.find(d => d.value === durationMinutes);
    if (durationOption && durationOption.injects !== injectCount) {
      setValue('injectCount', durationOption.injects);
    }
  }, [durationMinutes, injectCount, setValue]);

  // Auto-populate principle mapping when inject count changes
  React.useEffect(() => {
    if (injectCount && selectedPrinciples.length !== injectCount) {
      const newPrinciples = Array.from({ length: injectCount }, (_, index) => ({
        order: index + 1,
        principleKey: '',
        principleLabel: ''
      }));
      setValue('selectedPrinciples', newPrinciples);
    }
  }, [injectCount, selectedPrinciples.length, setValue]);

  const useRecommendedOrder = () => {
    const recommended = RECOMMENDED_PRINCIPLE_ORDERS[injectCount] || [];
    const newPrinciples = Array.from({ length: injectCount }, (_, index) => ({
      order: index + 1,
      principleKey: recommended[index] || '',
      principleLabel: CRISIS_PRINCIPLES[recommended[index]] || ''
    }));
    setValue('selectedPrinciples', newPrinciples);
  };

  const updatePrinciple = (index: number, principleKey: string) => {
    const newPrinciples = [...selectedPrinciples];
    newPrinciples[index] = {
      order: index + 1,
      principleKey,
      principleLabel: CRISIS_PRINCIPLES[principleKey] || ''
    };
    setValue('selectedPrinciples', newPrinciples);
  };

  const onSubmit = async (data: WizardFormData) => {
    if (!user) return;

    // Convert injectCount to number for comparison
    const injectCountNum = Number(data.injectCount);

    // Validate principle mapping
    if (!data.selectedPrinciples || data.selectedPrinciples.length !== injectCountNum) {
      setError(`Please map principles to all ${injectCountNum} injects (currently have ${data.selectedPrinciples?.length || 0})`);
      return;
    }
    
    const hasEmptyPrinciples = data.selectedPrinciples.some(p => !p.principleKey || p.principleKey.trim() === '');
    if (hasEmptyPrinciples) {
      setError('Please select a crisis management principle for each inject before generating');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      // Enhanced progress tracking
      setProgressStage('analyzing');
      setProgressPercent(10);
      setProgress('Analyzing context and requirements...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setProgressStage('mapping');
      setProgressPercent(25);
      setProgress('Mapping principles to injects...');
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setProgressStage('generating');
      setProgressPercent(50);
      setProgress(`Generating ${generationMode} exercise pack...`);
      
      // Call Cloud Function with generation mode and extended timeout
      const generateExercise = httpsCallable(functions, 'generateExercise', {
        timeout: 300000 // 5 minutes timeout
      });
      const dataWithCorrectTypes = {
        ...data,
        injectCount: injectCountNum,
        durationMinutes: Number(data.durationMinutes),
        generationMode: generationMode
      };
      
      console.log('Generating with mode:', generationMode);
      console.log('Form data:', dataWithCorrectTypes);
      
      const result = await generateExercise(dataWithCorrectTypes);
      
      setProgressStage('building');
      setProgressPercent(75);
      setProgress('Building facilitator materials...');
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (result.data.success) {
        setProgressStage('complete');
        setProgressPercent(100);
        setProgress('Exercise pack ready!');
        
        setTimeout(() => {
          navigate(`/exercises/${result.data.exerciseId}`);
        }, 1000);
      } else {
        throw new Error('Failed to generate exercise');
      }

    } catch (error: any) {
      setError(error.message || 'Failed to generate exercise. Please try again.');
      console.error('Error generating exercise:', error);
    } finally {
      setTimeout(() => {
        setLoading(false);
        setProgress('');
        setProgressStage('analyzing');
        setProgressPercent(0);
      }, 1500);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f8fafc' }}>
      {/* Header */}
      <header style={{
        backgroundColor: '#2563eb',
        color: 'white',
        padding: '20px 0',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <div style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Button 
              onClick={() => navigate('/')}
              style={{
                marginRight: '16px',
                backgroundColor: 'white',
                color: '#2563eb',
                border: '1px solid white',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <ArrowLeft style={{ width: '16px', height: '16px', marginRight: '8px' }} />
              Back to Dashboard
            </Button>
            <div>
              <h1 style={{
                fontSize: '28px',
                fontWeight: 'bold',
                margin: '0 0 4px 0'
              }}>Exercise Wizard</h1>
              <p style={{
                fontSize: '16px',
                opacity: '0.9',
                margin: '0'
              }}>AI-powered crisis exercise generator</p>
            </div>
          </div>
        </div>
      </header>

      {/* Wizard Form */}
      <main style={{
        maxWidth: '1200px',
        margin: '0 auto',
        padding: '32px 20px'
      }}>
        <form onSubmit={handleSubmit(onSubmit)} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '32px'
        }}>
          {error && (
            <div style={{
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: '6px'
            }}>
              {error}
            </div>
          )}

          {loading && (
            <div style={{
              backgroundColor: '#eff6ff',
              border: '1px solid #bfdbfe',
              color: '#1d4ed8',
              padding: '16px',
              borderRadius: '6px',
              textAlign: 'center'
            }}>
              <div style={{ marginBottom: '8px', fontWeight: '500' }}>
                Generating Your Exercise Pack...
              </div>
              <div style={{ fontSize: '14px' }}>{progress}</div>
            </div>
          )}

          {/* Section 1: Context & Audience */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Context & Audience</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>Who is this exercise for and where will it take place?</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }} title="The business sector this exercise is designed for. This influences the types of scenarios, stakeholders, and compliance requirements that will be included in your exercise.">
                    Industry *
                  </label>
                  <select
                    {...register('industry', { required: 'Industry is required' })}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    {INDUSTRY_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.industry && (
                    <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {errors.industry.message}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }} title="The geographic location or market where this crisis would occur. This affects local regulations, time zones, cultural considerations, and available emergency services.">
                    Geography / Market *
                  </label>
                  <Input
                    {...register('geography', { required: 'Geography is required' })}
                    placeholder="e.g., Australia - Victoria, Singapore, Hong Kong"
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  {errors.geography && (
                    <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {errors.geography.message}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }} title="Who will be participating in this exercise? Different audiences require different levels of complexity, terminology, and decision-making authority in the scenarios.">
                    Audience *
                  </label>
                  <select
                    {...register('audience', { required: 'Audience is required' })}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    {AUDIENCE_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.audience && (
                    <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {errors.audience.message}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 2: Scenario Configuration */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Scenario Configuration</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>What type of crisis scenario and exercise duration?</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }} title="The type of crisis event that will form the foundation of your exercise. Each scenario type has unique characteristics, response protocols, and decision points.">
                    Scenario Type *
                  </label>
                  <select
                    {...register('scenarioType', { required: 'Scenario type is required' })}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    {SCENARIO_OPTIONS.map(option => (
                      <option key={option} value={option}>{option}</option>
                    ))}
                  </select>
                  {errors.scenarioType && (
                    <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {errors.scenarioType.message}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }} title="How long the exercise will run from start to finish. Longer exercises allow for more complex scenarios and deeper learning, but require more participant time commitment.">
                    Duration *
                  </label>
                  <select
                    {...register('durationMinutes', { required: 'Duration is required' })}
                    style={{
                      width: '100%',
                      height: '40px',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px',
                      backgroundColor: 'white'
                    }}
                  >
                    {DURATION_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {errors.durationMinutes && (
                    <p style={{ color: '#dc2626', fontSize: '12px', marginTop: '4px' }}>
                      {errors.durationMinutes.message}
                    </p>
                  )}
                </div>

                <div>
                  <label style={{
                    display: 'block',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: '#374151',
                    marginBottom: '4px'
                  }} title="How many individual crisis events or information updates will occur during the exercise. More injects create a more complex and challenging experience. This automatically adjusts based on duration.">
                    Number of Injects
                  </label>
                  <Input
                    type="number"
                    {...register('injectCount', { min: 1, max: 10 })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      fontSize: '14px'
                    }}
                  />
                  <p style={{ color: '#6b7280', fontSize: '12px', marginTop: '4px' }}>
                    Auto-set based on duration, but adjustable
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Section 3: Principle Mapping */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Principle Mapping</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>Map crisis management principles to each inject for structured learning</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '16px'
            }}>
              <div style={{ marginBottom: '16px' }}>
                <Button
                  type="button"
                  onClick={useRecommendedOrder}
                  title="Automatically fills in the crisis management principles in a pedagogically optimal order, starting with foundational concepts and building to advanced decision-making skills."
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#f3f4f6',
                    color: '#374151',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <RotateCcw style={{ width: '16px', height: '16px' }} />
                  Use Recommended Order
                </Button>
              </div>

              <div style={{
                display: 'grid',
                gap: '12px'
              }}>
                {selectedPrinciples.map((principle, index) => (
                  <div key={index} style={{
                    display: 'grid',
                    gridTemplateColumns: '150px 1fr',
                    gap: '16px',
                    alignItems: 'center',
                    padding: '12px',
                    border: '1px solid #e5e7eb',
                    borderRadius: '6px',
                    backgroundColor: '#f9fafb'
                  }}>
                    <div 
                      style={{
                        fontWeight: '500',
                        color: '#374151'
                      }}
                      title={`Inject ${index + 1} will be delivered ${index * 15} minutes after the exercise starts. Each inject presents new information and challenges to test specific crisis management skills.`}
                    >
                      Inject {index + 1} (T+{index * 15}min)
                    </div>
                    <select
                      value={principle.principleKey}
                      onChange={(e) => updatePrinciple(index, e.target.value)}
                      title="Choose which crisis management principle this inject will focus on teaching. Each principle develops specific skills: See-Think-Act builds decision-making discipline, Stakeholder Balance teaches communication prioritization, Rule of Five identifies knowledge gaps and actionable priorities, etc."
                      style={{
                        width: '100%',
                        height: '40px',
                        padding: '8px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        fontSize: '14px',
                        backgroundColor: 'white'
                      }}
                    >
                      <option value="">Select a principle...</option>
                      {Object.entries(CRISIS_PRINCIPLES).map(([key, label]) => (
                        <option key={key} value={key}>{label}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
              
              {selectedPrinciples.some(p => !p.principleKey) && (
                <p style={{ color: '#dc2626', fontSize: '14px', marginTop: '8px' }}>
                  Please select a principle for each inject
                </p>
              )}
            </CardContent>
          </Card>

          {/* Section 4: Optional Configuration */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '24px 24px 16px 24px' }}>
              <CardTitle style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: '#111827',
                margin: '0 0 8px 0'
              }}>Optional Configuration</CardTitle>
              <CardDescription style={{
                color: '#6b7280',
                fontSize: '14px',
                margin: '0'
              }}>Additional settings to customize your exercise</CardDescription>
            </CardHeader>
            <CardContent style={{
              padding: '0 24px 24px 24px',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px'
            }}>
              {/* Learning Objectives */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }} title="Specific goals for what participants should learn or be able to do after completing this exercise. These guide the AI in creating targeted scenarios and decision points.">
                  Learning Objectives
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '8px'
                }}>
                  {COMMON_OBJECTIVES.map((objective) => (
                    <label 
                      key={objective} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                      title={OBJECTIVE_TOOLTIPS[objective] || objective}
                    >
                      <input
                        type="checkbox"
                        value={objective}
                        {...register('objectives')}
                        style={{
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>{objective}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Risk Dial */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }} title="Controls how much pressure and time stress the AI builds into your exercise. Conservative = methodical pace with time to think. Aggressive = fast-moving, high-pressure decisions with multiple simultaneous challenges.">
                  Risk Approach: Conservative ← → Aggressive
                </label>
                <input
                  type="range"
                  min="1"
                  max="5"
                  {...register('riskDial')}
                  title="Drag to adjust the intensity level: 1 = Very Conservative (methodical, plenty of thinking time), 3 = Balanced (realistic pressure), 5 = High Pressure (rapid decisions, overlapping challenges)"
                  style={{
                    width: '100%',
                    accentColor: '#2563eb'
                  }}
                />
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '12px',
                  color: '#6b7280',
                  marginTop: '4px'
                }}>
                  <span>Conservative (1)</span>
                  <span>Balanced (3)</span>
                  <span>Aggressive (5)</span>
                </div>
              </div>

              {/* Stakeholders */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }} title="Which stakeholder groups should be prominently featured in the exercise scenarios. This ensures the exercise addresses the specific relationships and communication challenges your organization faces.">
                  Key Stakeholders to Emphasize
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                  gap: '8px'
                }}>
                  {STAKEHOLDER_OPTIONS.map((stakeholder) => (
                    <label 
                      key={stakeholder} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                      title={STAKEHOLDER_TOOLTIPS[stakeholder] || stakeholder}
                    >
                      <input
                        type="checkbox"
                        value={stakeholder}
                        {...register('stakeholders')}
                        style={{
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>{stakeholder}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Compliance Frameworks */}
              <div>
                <label style={{
                  display: 'block',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  marginBottom: '8px'
                }} title="Regulatory or standards frameworks that should be tested during the exercise. The AI will include specific decision points and timelines related to these compliance requirements (e.g., APRA notification timelines, ISO22301 procedures).">
                  Compliance Frameworks to Test
                </label>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                  gap: '8px'
                }}>
                  {COMPLIANCE_OPTIONS.map((framework) => (
                    <label 
                      key={framework} 
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer'
                      }}
                      title={COMPLIANCE_TOOLTIPS[framework] || framework}
                    >
                      <input
                        type="checkbox"
                        value={framework}
                        {...register('complianceFrameworks')}
                        style={{
                          borderRadius: '4px',
                          border: '1px solid #d1d5db',
                          accentColor: '#2563eb'
                        }}
                      />
                      <span style={{ fontSize: '14px', color: '#374151' }}>{framework}</span>
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Generation Mode Selector */}
          <Card style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            border: '1px solid #e5e7eb'
          }}>
            <CardHeader style={{ padding: '20px 20px 16px 20px' }}>
              <CardTitle style={{ fontSize: '18px', fontWeight: 'bold', color: '#111827', margin: '0' }}>
                Generation Mode
              </CardTitle>
              <CardDescription style={{ color: '#6b7280', fontSize: '14px', margin: '4px 0 0 0' }}>
                Choose between draft (fast) or final (validated) generation
              </CardDescription>
            </CardHeader>
            <CardContent style={{ padding: '0 20px 20px 20px' }}>
              <div style={{ display: 'flex', gap: '16px' }}>
                <label 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    border: generationMode === 'draft' ? '2px solid #2563eb' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: generationMode === 'draft' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    flex: 1
                  }}
                  title="Draft Mode uses GPT-4o-mini for faster generation (15-30 seconds). Perfect for testing ideas, getting quick previews, or iterating on different scenarios. The quality is good but may need refinement."
                >
                  <input
                    type="radio"
                    value="draft"
                    checked={generationMode === 'draft'}
                    onChange={(e) => setGenerationMode(e.target.value as 'draft' | 'final')}
                    style={{ accentColor: '#2563eb' }}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Draft Mode</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Fast generation for iteration</div>
                  </div>
                </label>
                <label 
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px 16px',
                    border: generationMode === 'final' ? '2px solid #2563eb' : '1px solid #d1d5db',
                    borderRadius: '8px',
                    backgroundColor: generationMode === 'final' ? '#eff6ff' : 'white',
                    cursor: 'pointer',
                    flex: 1
                  }}
                  title="Final Mode uses GPT-4o with comprehensive validation and schema checking (45-90 seconds). Produces professional-quality exercises ready for immediate delivery. Includes structured JSON output and quality assurance."
                >
                  <input
                    type="radio"
                    value="final"
                    checked={generationMode === 'final'}
                    onChange={(e) => setGenerationMode(e.target.value as 'draft' | 'final')}
                    style={{ accentColor: '#2563eb' }}
                  />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827' }}>Final Mode</div>
                    <div style={{ fontSize: '12px', color: '#6b7280' }}>Validated professional quality</div>
                  </div>
                </label>
              </div>
            </CardContent>
          </Card>

          {/* Progress Display */}
          {loading && (
            <GenerationProgress 
              stage={progressStage}
              progress={progressPercent}
              message={progress}
            />
          )}

          {/* Generate Button */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            paddingTop: '16px'
          }}>
            <Button 
              type="button" 
              onClick={() => navigate('/')}
              style={{
                padding: '10px 20px',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                backgroundColor: 'white',
                color: '#374151',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500'
              }}
            >
              Cancel
            </Button>
            
            <Button 
              type="submit" 
              disabled={loading}
              title="Uses AI to generate a complete crisis exercise based on your inputs, including scenario narrative, principle-based injects, role briefs, facilitator scripts, and debrief prompts. This typically takes 30-60 seconds."
              style={{
                padding: '16px 32px',
                backgroundColor: loading ? '#9ca3af' : '#2563eb',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}
            >
              <Wand2 style={{ width: '20px', height: '20px' }} />
              {loading ? 'Generating Exercise Pack...' : 'Generate Exercise Pack'}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

